/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState
} from 'react';
import {
    Context
} from 'shared/context/GlobalState';
import {
    useParams
} from 'react-router-dom';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import LongTextField from '../primitives/LongTextField';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
    cn
} from 'shared/lib/utils';
import {
    Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2, Check,
    AlertCircle, Code, RotateCcw, ArrowLeft, ChevronRight, Workflow, HelpCircle
} from 'lucide-react';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    notify
} from 'shared/api/notifier';
import {
    sanitizeOpcodeForCreate, sanitizeOpcodeForUpdate
} from 'builders/builderOpcode/jsonMode/templates';
import {
    V2OpCodeBuilderProvider
} from 'builders/builderOpcode/configMode/v2/V2OpCodeBuilderContext';
import {
    buildV2Step, changeStepKind
} from 'builders/builderOpcode/configMode/v2/V2StepBuilder';
import TypedInputsEditor from 'builders/builderOpcode/configMode/v2/components/TypedInputsEditor';
import TypedOutputsEditor from 'builders/builderOpcode/configMode/v2/components/TypedOutputsEditor';
import DefinitionsEditor from 'builders/builderOpcode/configMode/v2/components/DefinitionsEditor';
import FlowsEditor from 'builders/builderOpcode/configMode/v2/components/FlowsEditor';
import v2Handler from 'builders/builderOpcode/configMode/v2/V2OpCodeBuilderHandler';
import dispatchInlineStep from './inline-steps/dispatchInlineStep';
import {
    stripParallelControlFlow
} from './inline-steps/_shared';
import {
    LlmStepZone, llmFocusInputClass, ZONE_LAYOUT_STEP_KINDS, STEP_TITLE_PLACEHOLDERS
} from './inline-steps/llmStepLayout';
import HooksAdvanced from './HooksAdvanced';
import EntityJsonView from '../components/EntityJsonView';
import {
    buildOelContext, collectSubflowWithKeys
} from '../primitives/oel';
import {
    apiManagerService
} from 'shared/api/services/BEX/apiManager.service';
import {
    OpcodeEditorModeProvider, useOpcodeEditorMode
} from './OpcodeEditorModeContext';
import {
    NAV_LABELS, FIELD_HELP, stepKindLabel, formatStepListLabel, formatStepKindOptionLabel
} from './copy';
import DependenciesSummary from './components/DependenciesSummary';
import {
    pruneUnusedEmptyDefinitions
} from './pruneOpcodeGraph';
import OpcodeSimpleLeftPanel from './OpcodeSimpleLeftPanel';
import OpcodeSimpleFlowPanel from './OpcodeSimpleFlowPanel';
import OpcodeStepKindPicker from './OpcodeStepKindPicker';
import {
    STEP_KINDS, KIND_INDEX
} from './stepKindCatalog';
import {
    getStepKindHelp
} from './stepKindHelp';
import {
    hydrateOpcodeFromDoc, isOpcodeDocComplete, normalizeFlowOutput
} from './hydrateOpcodeFromDoc';

/**
 * InlineOpcodeEditor — compact, accordion-friendly opcode editor.
 *
 * Mounted INSIDE AssistantWorkspace's OpCodes section. Two-pane layout:
 *   left rail = STEPS chip-list + OTHER (Typed I/O · Resource Manifest ·
 *   Governance · Metadata) ; right pane = IDENTITY (always) + selected
 *   item editor.
 *
 * Reuses the schema-aligned A-0008.03 internals (V2StepDispatcher,
 * TypedInputsEditor, TypedOutputsEditor, validateOpcode, sanitize*) so
 * the FULL opcode schema is editable. Used in AssistantWorkspace and Library.
 */

const InlineOpcodeEditorInner = forwardRef(function InlineOpcodeEditorInner({
    opcodeId, onSaved, onDeleted, onLiveInputsChange, initialDoc, onPristineChange
}, ref) {
    const {
        oragID, subOragID
    } = useParams();

    /* ---------------- State ---------------- */
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pristine, setPristine] = useState(true);
    const [statusFlash, setStatusFlash] = useState(null); // {kind, text}

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [version, setVersion] = useState('0.1.0');
    const [category, setCategory] = useState('');
    const [defaultStepTimeoutMs, setDefaultStepTimeoutMs] = useState(undefined);
    const [tags, setTags] = useState([]);

    const [inputs, setInputs] = useState([]);
    const [outputs, setOutputs] = useState([]);
    const [steps, setSteps] = useState([]);
    const [definitions, setDefinitions] = useState([]);
    const [flows, setFlows] = useState([]);

    const [riskLevel, setRiskLevel] = useState('moderate');
    const [requiresApproval, setRequiresApproval] = useState(false);
    const [piiHandling, setPiiHandling] = useState([]);
    const [complianceTags, setComplianceTags] = useState([]);
    // Opcode-level hooks block (OPCODE-SCHEMA §14.3 Future-slot-V1 — stored, not dispatched in V1)
    const [hooks, setHooks] = useState(undefined);

    const {
        user
    } = useContext(Context);
    const resolveUser = (id) => {
        if (!id) return '—';
        if (user?._id === id) return user?.name ?? id;
        return id.slice(-8);
    };

    const [serverOpcodeId, setServerOpcodeId] = useState(undefined);
    const [audit, setAudit] = useState(undefined);
    const [resourcesUsed, setResourcesUsed] = useState(undefined);
    const [engineCapabilities, setEngineCapabilities] = useState(undefined);

    /* selectedItem: {type:'step', idx:number} | {type:'other', key:string} */
    const [selectedItem, setSelectedItem] = useState(null);
    const [editingFlowIdx, setEditingFlowIdx] = useState(null);
    const [showKindPicker, setShowKindPicker] = useState(false);
    const [kindPickerTarget, setKindPickerTarget] = useState(null);
    const [showJson, setShowJson] = useState(false);

    const {
        isSimple, isAdvanced, setMode, mode
    } = useOpcodeEditorMode();

    function applyHydrated(h) {
        if (!h) return;
        setTitle(h.title);
        setDescription(h.description);
        setVersion(h.version);
        setCategory(h.category);
        setDefaultStepTimeoutMs(h.defaultStepTimeoutMs);
        setTags(h.tags);
        setInputs(h.inputs);
        setOutputs(h.outputs);
        setSteps(h.steps);
        setDefinitions(h.definitions);
        setFlows(h.flows);
        setHooks(h.hooks);
        setRiskLevel(h.riskLevel);
        setRequiresApproval(h.requiresApproval);
        setPiiHandling(h.piiHandling);
        setComplianceTags(h.complianceTags);
        setServerOpcodeId(h.serverOpcodeId);
        setAudit(h.audit);
        setResourcesUsed(h.resourcesUsed);
        setEngineCapabilities(h.engineCapabilities);
        setSelectedItem(h.selectedItem);
        setPristine(true);
    }

    /* ---------------- Load ---------------- */
    useEffect(() => {
        if (!opcodeId || !oragID || !subOragID) return;
        const initialMatch = initialDoc && initialDoc.opcode_id === opcodeId;
        const canHydrateInitial = initialMatch && isOpcodeDocComplete(initialDoc);
        if (canHydrateInitial) {
            applyHydrated(hydrateOpcodeFromDoc(initialDoc));
            setLoading(false);
        } else {
            setLoading(true);
        }
        let cancelled = false;
        jsonOpcodeService.getJSONOpcodeById({
            organizationId: oragID,
            subOrganizationId: subOragID,
            opcodeId
        }).then((res) => {
            if (cancelled) return;
            const d = res?.data || res;
            if (!d) return;
            applyHydrated(hydrateOpcodeFromDoc(d));
        }).catch((err) => {
            if (!cancelled) {
                setStatusFlash({
                    kind: 'error',
                    text: 'Load failed: ' + (err?.message || String(err))
                });
            }
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [opcodeId, oragID, subOragID, initialDoc]);

    useEffect(() => {
        onPristineChange?.(pristine);
    }, [pristine, onPristineChange]);

    /* ---------------- Step mutations ---------------- */
    function addStep(kind, stepDescription) {
        const built = buildV2Step(kind, steps);
        const desc = stepDescription?.trim() || stepKindLabel(kind, 'simple');
        built.description = desc;
        const next = [...steps, built];
        setSteps(next);
        setSelectedItem({
            type: 'step',
            idx: next.length - 1
        });
        setShowKindPicker(false);
        setPristine(false);
    }

    function startRagAnswer() {
        const s1 = buildV2Step('rag', steps);
        s1.description = 'Search knowledge';
        const s2 = buildV2Step('llm', [...steps, s1]);
        s2.description = 'Generate answer';
        const next = [...steps, s1, s2];
        setSteps(next);
        setSelectedItem({ type: 'step', idx: next.length - 1 });
        setPristine(false);
    }
    function startApiSummarize() {
        const s1 = buildV2Step('api', steps);
        s1.description = 'Call API';
        const s2 = buildV2Step('llm', [...steps, s1]);
        s2.description = 'Summarize response';
        const next = [...steps, s1, s2];
        setSteps(next);
        setSelectedItem({ type: 'step', idx: next.length - 1 });
        setPristine(false);
    }
    function startLlmOnly() {
        addStep('llm', 'Ask AI');
    }
    function removeStep(idx) {
        const next = steps.filter((_, i) => i !== idx).map((s, i) => ({
            ...s,
            id: s.id || `step_${i + 1}`
        }));
        setSteps(next);
        if (selectedItem?.type === 'step' && selectedItem.idx === idx) {
            setSelectedItem(next.length > 0 ? {
                type: 'step',
                idx: Math.min(idx, next.length - 1)
            } : null);
        }
        setPristine(false);
    }
    function moveStep(idx, dir) {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= steps.length) return;
        const next = [...steps];
        [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
        setSteps(next);
        if (selectedItem?.type === 'step') {
            if (selectedItem.idx === idx) setSelectedItem({
                type: 'step',
                idx: newIdx
            });
            else if (selectedItem.idx === newIdx) setSelectedItem({
                type: 'step',
                idx
            });
        }
        setPristine(false);
    }
    function patchStep(idx, updated) {
        setSteps(steps.map((s, i) => (i === idx ? updated : s)));
        setPristine(false);
    }
    function changeKind(idx, newKind) {
        patchStep(idx, changeStepKind(steps[idx], newKind, steps));
    }

    function openKindPicker(target) {
        setKindPickerTarget(target);
        setShowKindPicker(true);
    }

    function enterFlowEditor(flowIdx) {
        if (!flows[flowIdx]) return;
        setEditingFlowIdx(flowIdx);
        setSelectedItem(null);
    }

    function exitFlowEditor() {
        setEditingFlowIdx(null);
        setSelectedItem(null);
    }

    function addFlow() {
        const newFlow = {
            id: `flow_${flows.length + 1}`,
            steps: [],
            output: []
        };
        const next = [...flows, newFlow];
        const flowIdx = next.length - 1;
        setFlows(next);
        setEditingFlowIdx(flowIdx);
        setSelectedItem(null);
        setPristine(false);
    }

    function removeFlow(flowIdx) {
        const next = flows.filter((_, i) => i !== flowIdx);
        setFlows(next);
        if (editingFlowIdx === flowIdx) {
            const ni = next.length ? Math.min(flowIdx, next.length - 1) : null;
            setEditingFlowIdx(ni);
            setSelectedItem(null);
        } else if (editingFlowIdx != null && editingFlowIdx > flowIdx) {
            setEditingFlowIdx(editingFlowIdx - 1);
        }
        if (selectedItem?.type === 'flowStep') {
            if (selectedItem.flowIdx === flowIdx) {
                setSelectedItem(null);
            } else if (selectedItem.flowIdx > flowIdx) {
                setSelectedItem({
                    type: 'flowStep',
                    flowIdx: selectedItem.flowIdx - 1,
                    idx: selectedItem.idx
                });
            }
        }
        setPristine(false);
    }

    function patchFlow(flowIdx, patch) {
        setFlows(flows.map((f, i) => (i === flowIdx ? { ...f, ...patch } : f)));
        setPristine(false);
    }

    function addFlowStep(flowIdx, kind, stepDescription) {
        const flow = flows[flowIdx];
        if (!flow) return;
        const flowSteps = flow.steps || [];
        const built = buildV2Step(kind, flowSteps);
        built.description = stepDescription?.trim() || stepKindLabel(kind, 'simple');
        const nextSteps = [...flowSteps, built];
        patchFlow(flowIdx, { steps: nextSteps });
        setEditingFlowIdx(flowIdx);
        setSelectedItem({ type: 'flowStep', flowIdx, idx: nextSteps.length - 1 });
        setShowKindPicker(false);
    }

    function patchFlowStep(flowIdx, stepIdx, updated) {
        const flow = flows[flowIdx];
        if (!flow) return;
        const nextSteps = (flow.steps || []).map((s, i) => (i === stepIdx ? updated : s));
        patchFlow(flowIdx, { steps: nextSteps });
    }

    function removeFlowStep(flowIdx, stepIdx) {
        const flow = flows[flowIdx];
        if (!flow) return;
        const nextSteps = (flow.steps || []).filter((_, i) => i !== stepIdx);
        patchFlow(flowIdx, { steps: nextSteps });
        if (selectedItem?.type === 'flowStep' && selectedItem.flowIdx === flowIdx && selectedItem.idx === stepIdx) {
            setSelectedItem(nextSteps.length > 0 ? {
                type: 'flowStep',
                flowIdx,
                idx: Math.min(stepIdx, nextSteps.length - 1)
            } : null);
        }
    }

    function changeFlowStepKind(flowIdx, stepIdx, newKind) {
        const flow = flows[flowIdx];
        if (!flow) return;
        const flowSteps = flow.steps || [];
        patchFlowStep(flowIdx, stepIdx, changeStepKind(flowSteps[stepIdx], newKind, flowSteps));
    }

    function addDefinition(kind, stepDescription) {
        const built = buildV2Step(kind, definitions);
        built.id = `def_${definitions.length + 1}`;
        built.description = stepDescription?.trim() || stepKindLabel(kind, 'simple');
        const next = [...definitions, built];
        setDefinitions(next);
        setSelectedItem({ type: 'definition', idx: next.length - 1 });
        setShowKindPicker(false);
        setPristine(false);
    }

    function removeDefinition(idx) {
        const next = definitions.filter((_, i) => i !== idx);
        setDefinitions(next);
        if (selectedItem?.type === 'definition') {
            if (selectedItem.idx === idx) {
                setSelectedItem(next.length
                    ? { type: 'definition', idx: Math.min(idx, next.length - 1) }
                    : null);
            } else if (selectedItem.idx > idx) {
                setSelectedItem({ type: 'definition', idx: selectedItem.idx - 1 });
            }
        }
        setPristine(false);
    }

    function patchDefinition(idx, updated) {
        setDefinitions(definitions.map((d, i) => (i === idx ? updated : d)));
        setPristine(false);
    }

    function changeDefinitionKind(idx, newKind) {
        patchDefinition(idx, changeStepKind(definitions[idx], newKind, definitions));
    }

    function handleKindSelect(kind) {
        const target = kindPickerTarget;
        if (!target || target.scope === 'procedure') addStep(kind);
        else if (target.scope === 'flow') addFlowStep(target.flowIdx, kind);
        else if (target.scope === 'definition') addDefinition(kind);
        setKindPickerTarget(null);
    }

    /* ---------------- Save ---------------- */
    async function handleSave() {
        const cleanedDefinitions = pruneUnusedEmptyDefinitions(definitions, steps, flows);
        if (cleanedDefinitions.length !== definitions.length) {
            setDefinitions(cleanedDefinitions);
        }

        const opcode = {
            title: title.trim(),
            description,
            ...(tags.length ? {
                tags
            } : {}),
            ...(category ? {
                category
            } : {}),
            ...(version ? {
                version
            } : {}),
            ...(typeof defaultStepTimeoutMs === 'number' ? {
                default_step_timeout_ms: defaultStepTimeoutMs
            } : {}),
            inputs,
            outputs,
            // Schema §6.3: parallel rejects `when` / `for` / `repeat_until` on
            // itself. Strip defensively so the server doesn't 400 on legacy data.
            steps: steps.map(stripParallelControlFlow),
            ...(cleanedDefinitions.length ? {
                definitions: cleanedDefinitions.map(stripParallelControlFlow)
            } : {}),
            ...(flows.length ? {
                flows
            } : {}),
            // Opcode-level hooks (Future-slot-V1 — stored, not dispatched by Mind yet)
            ...(hooks && Object.keys(hooks).length ? {
                hooks
            } : {}),
            governance_hints: {
                risk_level: riskLevel,
                requires_approval: requiresApproval,
                ...(piiHandling.length ? {
                    pii_handling: piiHandling
                } : {}),
                ...(complianceTags.length ? {
                    compliance_tags: complianceTags
                } : {}),
            }
        };

        const sanitized = sanitizeOpcodeForUpdate(opcode);
        const v = v2Handler.validateOpcode(sanitized);
        if (!v.isValid) {
            const hint = v.message?.includes('Definition')
                ? ' Check Reusable definitions — remove empty rows or complete required fields.'
                : '';
            const message = (v.message || 'Validation failed.') + hint;
            setStatusFlash({
                kind: 'error',
                text: message
            });
            notify(message, 'danger', 10);
            return;
        }

        setSaving(true);
        setStatusFlash(null);
        try {
            await jsonOpcodeService.updateJSONOpcode({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcodeId,
                opcode: sanitized
            });
            setPristine(true);
            setStatusFlash({
                kind: 'success',
                text: 'Saved.'
            });
            setTimeout(() => setStatusFlash(null), 2000);
            // Walk the full step tree (top-level + parallel branches +
            // definitions + flows) to find every referenced RAG collection
            // and API id. Parent assistant uses these to keep
            // `knowledge[]` and `data_connections[]` in sync automatically.
            const referencedCollections = [];
            const referencedApis = [];
            const visit = (s) => {
                if (!s) return;
                if (s.kind === 'rag') {
                    const cid = s.inputs?.collection_id || s.collection_id;
                    if (cid && !referencedCollections.includes(cid)) referencedCollections.push(cid);
                }
                if (s.kind === 'api') {
                    const aid = s.api_id;
                    if (aid && !referencedApis.includes(aid)) referencedApis.push(aid);
                }
                if (Array.isArray(s.branches)) s.branches.forEach(visit);
            };
            steps.forEach(visit);
            cleanedDefinitions.forEach(visit);
            flows.forEach((f) => (f?.steps || []).forEach(visit));
            onSaved?.({
                opcode_id: opcodeId,
                title: opcode.title,
                description: opcode.description,
                version: opcode.version,
                category: opcode.category,
                inputs,
                outputs,
                steps,
                definitions: cleanedDefinitions,
                flows,
                steps_count: steps.length,
                kinds: [...new Set(steps.map(s => s.kind))],
                referencedCollections,
                referencedApis
            });
        } catch (err) {
            setStatusFlash({
                kind: 'error',
                text: 'Save failed: ' + (err?.response?.data?.message || err?.response?.data?.error || err?.message)
            });
        } finally {
            setSaving(false);
        }
    }

    /* Imperative handle — lets the parent AssistantWorkspace flush this
       opcode's unsaved edits to Redis before saving the assistant doc.
       Captures the latest pristine + handleSave through refs so the
       imperative method always reads current state.                       */
    const pristineRef = useRef(pristine);
    useEffect(() => { pristineRef.current = pristine; }, [pristine]);
    const handleSaveRef = useRef(handleSave);
    useEffect(() => { handleSaveRef.current = handleSave; });
    useImperativeHandle(ref, () => ({
        flushSave: async () => {
            if (pristineRef.current) return {
                ok: true,
                skipped: true
            };
            try { await handleSaveRef.current(); return {
                ok: true
            }; }
            catch (err) { return {
                ok: false,
                error: err
            }; }
        },
        isPristine: () => pristineRef.current
    }), []);

    async function handleDelete() {
        if (!window.confirm('Delete this opcode? This cannot be undone.')) return;
        try {
            await jsonOpcodeService.deleteJSONOpcodes({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcodeIds: [opcodeId]
            });
            onDeleted?.(opcodeId);
        } catch (err) {
            setStatusFlash({
                kind: 'error',
                text: 'Delete failed: ' + (err?.response?.data?.message || err?.message)
            });
        }
    }

    /* ---------------- Derived ---------------- */
    const currentStepIdx = (selectedItem?.type === 'step') ? selectedItem.idx : null;
    const currentFlowStepCtx = (selectedItem?.type === 'flowStep')
        ? { flowIdx: selectedItem.flowIdx, stepIdx: selectedItem.idx }
        : null;
    const currentDefinitionIdx = (selectedItem?.type === 'definition') ? selectedItem.idx : null;

    const [apiSchemaRegistry, setApiSchemaRegistry] = useState({});
    const [apiRegistryReload, setApiRegistryReload] = useState(0);
    useEffect(() => {
        const handler = () => setApiRegistryReload((n) => n + 1);
        window.addEventListener('bex:api-saved', handler);
        return () => window.removeEventListener('bex:api-saved', handler);
    }, []);
    useEffect(() => {
        let cancelled = false;
        if (!oragID || !subOragID) return undefined;
        apiManagerService.listApis(oragID, subOragID)
            .then((res) => {
                if (cancelled) return;
                const arr = res?.data || res || [];
                const reg = {};
                for (const a of (Array.isArray(arr) ? arr : [])) {
                    if (a?._id && a?.output_schema) {
                        reg[a._id] = typeof a.output_schema === 'string'
                            ? safeJson(a.output_schema)
                            : a.output_schema;
                    }
                }
                setApiSchemaRegistry(reg);
            })
            .catch(() => { /* registry stays empty — ok */ });
        return () => { cancelled = true; };
    }, [oragID, subOragID, apiRegistryReload]);

    const oelContext = useMemo(() => buildOelContext({
        inputs,
        steps,
        definitions,
        flows,
        currentStepIdx,
        apiSchemaRegistry
    }), [inputs, steps, definitions, flows, currentStepIdx, apiSchemaRegistry]);

    // Opcode-level outputs evaluate AFTER all steps — expose every step.
    const outputsOelContext = useMemo(() => buildOelContext({
        inputs,
        steps,
        definitions,
        flows,
        currentStepIdx: null,
        apiSchemaRegistry
    }), [inputs, steps, definitions, flows, apiSchemaRegistry]);

    const flowStepOelContext = useMemo(() => {
        if (!currentFlowStepCtx) return null;
        const flow = flows[currentFlowStepCtx.flowIdx];
        if (!flow) return null;
        const subflowParams = collectSubflowWithKeys(flow.id, { steps, flows }).map((name) => ({
            name,
            type: 'any',
            description: 'Passed from Subflow With (parameters)'
        }));
        return buildOelContext({
            inputs,
            steps: flow.steps || [],
            definitions,
            flows,
            currentStepIdx: currentFlowStepCtx.stepIdx,
            apiSchemaRegistry,
            subflowAvailable: true,
            subflowParams,
            inFlowScope: true,
            // Parent procedure steps are readable inside a choose/subflow-invoked
            // flow (engine ChainMap fallback) — expose them so @<parent_step_id>
            // validates and autocompletes.
            parentSteps: steps
        });
    }, [inputs, flows, definitions, currentFlowStepCtx, apiSchemaRegistry, steps]);

    const definitionOelContext = useMemo(() => {
        if (currentDefinitionIdx == null) return null;
        // Definitions run via call/choose with the parent procedure's step outputs
        // in scope — expose all main procedure steps (not other definitions).
        return buildOelContext({
            inputs,
            steps,
            definitions,
            flows,
            currentStepIdx: null,
            apiSchemaRegistry
        });
    }, [inputs, steps, definitions, flows, currentDefinitionIdx, apiSchemaRegistry]);

    const flowOutputOelContext = useMemo(() => {
        if (editingFlowIdx == null || !flows[editingFlowIdx]) return null;
        const flow = flows[editingFlowIdx];
        const subflowParams = collectSubflowWithKeys(flow.id, { steps, flows }).map((name) => ({
            name,
            type: 'any',
            description: 'Passed from Subflow With (parameters)'
        }));
        return buildOelContext({
            inputs,
            steps: flow.steps || [],
            definitions,
            flows,
            currentStepIdx: null,
            apiSchemaRegistry,
            subflowAvailable: true,
            subflowParams,
            inFlowScope: true,
            // Flow output expressions can also reference parent procedure steps.
            parentSteps: steps
        });
    }, [inputs, flows, definitions, editingFlowIdx, apiSchemaRegistry, steps]);

    /* ---------------- Render ---------------- */
    const selectedProcedureStepIdx = selectedItem?.type === 'step' ? selectedItem.idx : null;
    const selectedFlowStepIdx = selectedItem?.type === 'flowStep' ? selectedItem.idx : null;
    const selectedDefinitionIdx = selectedItem?.type === 'definition' ? selectedItem.idx : null;

    if (loading && !serverOpcodeId) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading opcode…</span>
            </div>
        );
    }

    const navLabel = (key) => NAV_LABELS[key]?.[mode] || key;
    const advancedTopOther = ['inputs', 'definitions', 'flows'];
    const advancedBottomOther = ['outputs', 'hooks', 'manifest', 'governance', 'metadata'];

    const renderOtherPanel = () => (
        <>
            {isAdvanced && selectedItem?.type === 'other' && selectedItem.key === 'inputs' && (
                <OtherBlock title={navLabel('inputs')}>
                    <TypedInputsEditor
                        inputs={inputs}
                        onChange={(v) => { setInputs(v); setPristine(false); onLiveInputsChange?.(opcodeId, v); }}
                    />
                </OtherBlock>
            )}
            {isAdvanced && selectedItem?.type === 'other' && selectedItem.key === 'outputs' && (
                <OtherBlock title={navLabel('outputs')}>
                    <TypedOutputsEditor
                        outputs={outputs}
                        onChange={(v) => { setOutputs(v); setPristine(false); }}
                        oelContext={outputsOelContext}
                    />
                </OtherBlock>
            )}
            {selectedItem?.type === 'other' && selectedItem.key === 'definitions' && (
                <OtherBlock title={`${navLabel('definitions')}${definitions.length ? ` (${definitions.length})` : ''}`}>
                    <DefinitionsEditor
                        definitions={definitions}
                        onChange={(v) => { setDefinitions(v); setPristine(false); }}
                        inputs={(inputs || []).map((i) => i?.name).filter(Boolean)}
                        steps={steps}
                    />
                </OtherBlock>
            )}
            {selectedItem?.type === 'other' && selectedItem.key === 'flows' && (
                <OtherBlock title={`${navLabel('flows')}${flows.length ? ` (${flows.length})` : ''}`}>
                    <FlowsEditor
                        flows={flows}
                        definitions={definitions}
                        onChange={(v) => { setFlows(v); setPristine(false); }}
                        inputs={(inputs || []).map((i) => i?.name).filter(Boolean)}
                    />
                </OtherBlock>
            )}
            {isAdvanced && selectedItem?.type === 'other' && selectedItem.key === 'hooks' && (
                <OtherBlock title={navLabel('hooks')} badge="informational in V1">
                    <HooksAdvanced
                        hooks={hooks}
                        onPatchOpcode={(partial) => {
                            if ('hooks' in partial) setHooks(partial.hooks);
                            setPristine(false);
                        }}
                    />
                </OtherBlock>
            )}
            {isAdvanced && selectedItem?.type === 'other' && selectedItem.key === 'manifest' && (
                <OtherBlock title={navLabel('manifest')} badge="auto · read-only">
                    <pre className="overflow-x-auto rounded border border-dashed bg-muted/30 p-2 font-mono text-[10.5px] text-muted-foreground">
                        {JSON.stringify({
                            resources_used: resourcesUsed || {
                                llms: [],
                                apis: [],
                                collections: [],
                                tools: [],
                                subflows: []
                            },
                            engine_capabilities_required: engineCapabilities || {
                                min_schema_version: '2.0',
                                requires: []
                            }
                        }, null, 2)}
                    </pre>
                    <p className="mt-1 text-[10.5px] text-muted-foreground">
                        Server re-derives after each save by inspecting steps.
                    </p>
                </OtherBlock>
            )}
            {isAdvanced && selectedItem?.type === 'other' && selectedItem.key === 'governance' && (
                <OtherBlock title={navLabel('governance')} badge="informational">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        <div>
                            <Label className="text-[11px]">Risk level</Label>
                            <Select value={riskLevel} onValueChange={(v) => { setRiskLevel(v); setPristine(false); }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="safe">safe</SelectItem>
                                    <SelectItem value="moderate">moderate</SelectItem>
                                    <SelectItem value="sensitive">sensitive</SelectItem>
                                    <SelectItem value="critical">critical</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[11px]">Requires approval</Label>
                            <Select value={requiresApproval ? 'yes' : 'no'} onValueChange={(v) => { setRequiresApproval(v === 'yes'); setPristine(false); }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="no">no</SelectItem>
                                    <SelectItem value="yes">yes</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <p className="mt-1 text-[10.5px] text-muted-foreground">
                        Saved with the opcode; not yet enforced at runtime.
                    </p>
                </OtherBlock>
            )}
            {isAdvanced && selectedItem?.type === 'other' && selectedItem.key === 'metadata' && (
                <OtherBlock title={navLabel('metadata')} badge="server-managed">
                    <div className="space-y-1 rounded border border-dashed bg-muted/30 p-2 font-mono text-[10.5px] text-muted-foreground">
                        <div>opcode_id: <span className="text-foreground">{serverOpcodeId || '—'}</span></div>
                        <div>created: <span className="text-foreground">{audit?.created || '—'}</span> by <span className="text-foreground">{resolveUser(audit?.createdBy)}</span></div>
                        <div>updated: <span className="text-foreground">{audit?.updated || '—'}</span> by <span className="text-foreground">{resolveUser(audit?.updatedBy)}</span></div>
                    </div>
                </OtherBlock>
            )}
        </>
    );

    const renderSimpleDetail = () => {
        if (selectedItem?.type === 'step' && steps[selectedItem.idx]) {
            return (
                <StepPane
                    idx={selectedItem.idx}
                    step={steps[selectedItem.idx]}
                    onPatchStep={(updated) => patchStep(selectedItem.idx, updated)}
                    onChangeKind={(k) => changeKind(selectedItem.idx, k)}
                    definitions={definitions}
                    flows={flows}
                    oelContext={oelContext}
                />
            );
        }
        if (selectedItem?.type === 'definition' && definitions[selectedItem.idx]) {
            const idx = selectedItem.idx;
            return (
                <StepPane
                    idx={idx}
                    step={definitions[idx]}
                    paneLabel="Definition"
                    onPatchStep={(updated) => patchDefinition(idx, updated)}
                    onChangeKind={(k) => changeDefinitionKind(idx, k)}
                    definitions={definitions}
                    flows={flows}
                    oelContext={definitionOelContext}
                />
            );
        }
        return (
            <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                Select a procedure step or definition on the left to configure it here.
            </div>
        );
    };

    const renderFlowDetail = () => {
        if (editingFlowIdx == null || !flows[editingFlowIdx]) return null;
        const flowIdx = editingFlowIdx;
        if (
            selectedItem?.type === 'flowStep'
            && selectedItem.flowIdx === flowIdx
            && flows[flowIdx].steps?.[selectedItem.idx]
        ) {
            const { idx } = selectedItem;
            const step = flows[flowIdx].steps[idx];
            return (
                <StepPane
                    idx={idx}
                    step={step}
                    paneLabel="Flow step"
                    onPatchStep={(updated) => patchFlowStep(flowIdx, idx, updated)}
                    onChangeKind={(k) => changeFlowStepKind(flowIdx, idx, k)}
                    definitions={definitions}
                    flows={flows}
                    oelContext={flowStepOelContext}
                />
            );
        }
        return (
            <div className="rounded-lg border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                Select a step under Procedure on the left, or add one.
            </div>
        );
    };

    const renderStepDetail = () => (
        <>
            {selectedItem?.type === 'step' && steps[selectedItem.idx] && (
                <StepPane
                    idx={selectedItem.idx}
                    step={steps[selectedItem.idx]}
                    onPatchStep={(updated) => patchStep(selectedItem.idx, updated)}
                    onChangeKind={(k) => changeKind(selectedItem.idx, k)}
                    definitions={definitions}
                    flows={flows}
                    oelContext={oelContext}
                />
            )}
            {renderOtherPanel()}
            {selectedItem === null && isAdvanced && (
                <div className="rounded border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
                    Pick a step (or section under Other) on the left to edit it.
                </div>
            )}
        </>
    );

    const editingFlow = editingFlowIdx != null ? flows[editingFlowIdx] : null;
    const inFlowEditor = isSimple && editingFlow;

    return (
        <V2OpCodeBuilderProvider>
            <div className="overflow-hidden rounded-lg border-2 border-border/60 bg-card shadow-sm">
                {/* Inline header — opcode or flow context + dirty + save */}
                <div
                    className={cn(
                        'flex flex-wrap items-center gap-2 border-b px-4 py-3',
                        inFlowEditor
                            ? 'border-primary/30 bg-primary/[0.06]'
                            : 'border-border bg-muted/40'
                    )}
                >
                    {inFlowEditor ? (
                        <>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="h-9 shrink-0 border-2 border-primary/40 bg-background px-3 text-sm font-semibold text-primary shadow-sm hover:bg-primary/10"
                                onClick={exitFlowEditor}
                            >
                                <ArrowLeft className="mr-1.5 h-4 w-4" />
                                Back to opcode
                            </Button>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-muted-foreground">
                                    <span className="truncate font-medium" title={title}>
                                        {title || 'Untitled opcode'}
                                    </span>
                                    <ChevronRight className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
                                    <span className="font-semibold text-foreground">Reusable flow</span>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
                                        <Workflow className="h-4 w-4" />
                                    </span>
                                    <span
                                        className="truncate font-mono text-base font-semibold"
                                        title={editingFlow.id || `flow_${editingFlowIdx + 1}`}
                                    >
                                        {editingFlow.id || `flow_${editingFlowIdx + 1}`}
                                    </span>
                                    <span className="rounded-md border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                                        Editing flow
                                    </span>
                                    {!pristine && (
                                        <span className="rounded bg-yellow-100 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-yellow-800">
                                            unsaved
                                        </span>
                                    )}
                                    {statusFlash && (
                                        <span className={cn(
                                            'flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold',
                                            statusFlash.kind === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        )}>
                                            {statusFlash.kind === 'success' ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                                            {statusFlash.text}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                    Sub-workflow inside this opcode — use <span className="font-semibold text-foreground">Back to opcode</span> to return to the main procedure.
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="truncate text-base font-semibold" title={title}>
                                    {title || 'Untitled opcode'}
                                </span>
                                {!pristine && (
                                    <span className="rounded bg-yellow-100 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-yellow-800">
                                        unsaved
                                    </span>
                                )}
                                {statusFlash && (
                                    <span className={cn(
                                        'flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold',
                                        statusFlash.kind === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    )}>
                                        {statusFlash.kind === 'success' ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                                        {statusFlash.text}
                                    </span>
                                )}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2">
                                <span className="text-[11px] text-muted-foreground font-mono">{serverOpcodeId}</span>
                                {isSimple && <DependenciesSummary resourcesUsed={resourcesUsed} />}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center rounded-md border bg-muted/30 p-0.5 text-xs">
                        <button
                            type="button"
                            className={cn(
                                'rounded px-2.5 py-1 font-medium text-muted-foreground transition-colors',
                                isSimple && 'bg-primary font-semibold text-primary-foreground shadow-sm'
                            )}
                            onClick={() => setMode('simple')}
                        >
                            Simple
                        </button>
                        <button
                            type="button"
                            className={cn(
                                'rounded px-2.5 py-1 font-medium text-muted-foreground transition-colors',
                                isAdvanced && 'bg-primary font-semibold text-primary-foreground shadow-sm'
                            )}
                            onClick={() => setMode('advanced')}
                        >
                            Advanced
                        </button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowJson(true)}>
                        <Code className="mr-1 h-3.5 w-3.5" /> JSON
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={handleDelete}>
                        <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                    </Button>
                    <Button onClick={handleSave} disabled={saving || pristine}>
                        {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                        Save opcode
                    </Button>
                </div>

                {/* Two-pane body
                    Mobile order: Identity(1) → Steps nav(2) → Step detail(3)
                    Desktop: CSS grid — aside spans both rows in col-1 */}
                {isSimple ? (
                    editingFlowIdx != null && flows[editingFlowIdx] ? (
                        <div className="grid grid-cols-1 gap-4 p-3 md:p-4 lg:grid-cols-3 lg:gap-6">
                            <div className="min-w-0 lg:col-span-1 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-1">
                                <OpcodeSimpleFlowPanel
                                    flow={flows[editingFlowIdx]}
                                    onFlowIdChange={(id) => patchFlow(editingFlowIdx, { id })}
                                    steps={flows[editingFlowIdx].steps || []}
                                    selectedStepIdx={selectedFlowStepIdx}
                                    onSelectStep={(i) => setSelectedItem({
                                        type: 'flowStep',
                                        flowIdx: editingFlowIdx,
                                        idx: i
                                    })}
                                    onAddStep={() => openKindPicker({
                                        scope: 'flow',
                                        flowIdx: editingFlowIdx
                                    })}
                                    onRemoveStep={(i) => removeFlowStep(editingFlowIdx, i)}
                                    outputs={normalizeFlowOutput(flows[editingFlowIdx].output)}
                                    onOutputsChange={(v) => patchFlow(editingFlowIdx, { output: v })}
                                    outputsOelContext={flowOutputOelContext}
                                />
                            </div>
                            <div className="min-w-0 lg:col-span-2 lg:border-l lg:border-foreground/20 lg:pl-8">
                                <div className="min-h-[min(70vh,520px)] lg:min-h-[480px]">
                                    {renderFlowDetail()}
                                </div>
                            </div>
                        </div>
                    ) : (
                    <div className="grid grid-cols-1 gap-4 p-3 md:p-4 lg:grid-cols-3 lg:gap-6">
                        {/* Left — overview (1/3) */}
                        <div className="min-w-0 lg:col-span-1 space-y-3 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-1">
                            <div className="space-y-3">
                                <InlineFieldGroup label="Title">
                                    <Input
                                        value={title}
                                        onChange={(e) => { setTitle(e.target.value); setPristine(false); }}
                                        placeholder="What this procedure does"
                                    />
                                </InlineFieldGroup>
                                <InlineFieldGroup label="Description">
                                    <LongTextField
                                        label="Description"
                                        value={description}
                                        onChange={(v) => { setDescription(v); setPristine(false); }}
                                        placeholder="Describe what happens when this runs"
                                        monospace={false}
                                        previewLines={2}
                                    />
                                </InlineFieldGroup>
                            </div>
                            <OpcodeSimpleLeftPanel
                                inputs={inputs}
                                onInputsChange={(v) => {
                                    setInputs(v);
                                    setPristine(false);
                                    onLiveInputsChange?.(opcodeId, v);
                                }}
                                outputs={outputs}
                                onOutputsChange={(v) => {
                                    setOutputs(v);
                                    setPristine(false);
                                }}
                                outputsOelContext={outputsOelContext}
                                steps={steps}
                                selectedStepIdx={selectedProcedureStepIdx}
                                onSelectStep={(i) => setSelectedItem({ type: 'step', idx: i })}
                                onAddStep={() => openKindPicker({ scope: 'procedure' })}
                                onRemoveStep={removeStep}
                                onStartRagAnswer={startRagAnswer}
                                onStartApiSummarize={startApiSummarize}
                                onStartLlmOnly={startLlmOnly}
                                flows={flows}
                                onSelectFlow={enterFlowEditor}
                                onAddFlow={addFlow}
                                onRemoveFlow={removeFlow}
                                definitions={definitions}
                                selectedDefinitionIdx={selectedDefinitionIdx}
                                onSelectDefinition={(i) => setSelectedItem({ type: 'definition', idx: i })}
                                onAddDefinition={() => openKindPicker({ scope: 'definition' })}
                                onRemoveDefinition={removeDefinition}
                            />
                        </div>

                        {/* Right — step / flow / definition detail (2/3) */}
                        <div className="min-w-0 lg:col-span-2 lg:border-l lg:border-foreground/20 lg:pl-8">
                            <div className="min-h-[min(70vh,520px)] lg:min-h-[480px]">
                                {renderSimpleDetail()}
                            </div>
                        </div>
                    </div>
                    )
                ) : (
                <div className="ds-opcode-body">
                    {/* Left rail */}
                    <aside className="ds-opcode-aside border-b md:border-b-0 md:border-r md:bg-muted/10">
                        <div className="p-4 space-y-5">
                            {/* Typed Inputs + Reusable building blocks (Definitions, Flows) */}
                            <div className="space-y-1.5">
                                {advancedTopOther.map((t) => {
                                    const key = t;
                                    const label = key === 'definitions'
                                        ? `${navLabel(key)}${definitions.length ? ` (${definitions.length})` : ''}`
                                        : key === 'flows'
                                            ? `${navLabel(key)}${flows.length ? ` (${flows.length})` : ''}`
                                            : navLabel(key);
                                    const sel = selectedItem?.type === 'other' && selectedItem.key === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setSelectedItem({ type: 'other', key })}
                                            className={cn(
                                                'block w-full rounded-md border bg-card px-3 py-2 text-left text-sm',
                                                sel ? 'border-primary bg-primary/5 shadow-sm font-medium' : 'hover:border-foreground/30'
                                            )}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Steps */}
                            <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{navLabel('steps')}</div>
                                <div className="space-y-1.5">
                                    {steps.length === 0 && (
                                        <div className="rounded-md border border-dashed bg-card p-3 text-center text-xs text-muted-foreground">
                                            No steps yet
                                        </div>
                                    )}
                                    {steps.map((s, i) => {
                                        const meta = KIND_INDEX[s.kind] || KIND_INDEX.llm;
                                        const Icon = meta.icon;
                                        const sel = selectedItem?.type === 'step' && selectedItem.idx === i;
                                        return (
                                            <div
                                                key={s.id || i}
                                                onClick={() => setSelectedItem({
                                                    type: 'step',
                                                    idx: i
                                                })}
                                                className={cn(
                                                    'group flex cursor-pointer items-center gap-2 rounded-md border bg-card px-2.5 py-2',
                                                    sel ? 'border-primary bg-primary/5 shadow-sm' : 'hover:border-foreground/30'
                                                )}
                                            >
                                                <span className={cn('inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-muted', meta.color)}>
                                                    <Icon className="h-3.5 w-3.5" />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-sm font-medium" title={formatStepListLabel(s)}>
                                                        {i + 1}. {formatStepListLabel(s)}
                                                    </div>
                                                    {s.id && (
                                                        <div className="text-[10.5px] font-mono text-muted-foreground truncate">
                                                            {s.id}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="hidden flex-shrink-0 items-center gap-0.5 group-hover:flex">
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); moveStep(i, -1); }} disabled={i === 0} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30">
                                                        <ArrowUp className="h-3 w-3" />
                                                    </button>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); moveStep(i, 1); }} disabled={i === steps.length - 1} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30">
                                                        <ArrowDown className="h-3 w-3" />
                                                    </button>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); removeStep(i); }} className="p-1 text-muted-foreground hover:text-destructive">
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowKindPicker(true)}
                                    className="mt-2 w-full rounded-md border border-dashed bg-card px-3 py-2 text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary"
                                >
                                    + Add step
                                </button>
                            </div>

                            {/* Typed Outputs + remaining others */}
                            <div className="space-y-1.5">
                                {advancedBottomOther.map((key) => {
                                    const sel = selectedItem?.type === 'other' && selectedItem.key === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setSelectedItem({ type: 'other', key })}
                                            className={cn(
                                                'block w-full rounded-md border bg-card px-3 py-2 text-left text-sm',
                                                sel ? 'border-primary bg-primary/5 shadow-sm font-medium' : 'hover:border-foreground/30'
                                            )}
                                        >
                                            {navLabel(key)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </aside>

                    {/* Identity — order-1 on mobile, col-2 row-1 on desktop */}
                    <section className="ds-opcode-identity p-4 md:p-6">
                        <section className="rounded-lg border bg-card shadow-sm">
                            <div className="border-b bg-muted/30 px-4 py-3">
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Identity</div>
                            </div>
                            <div style={{
                                background: 'var(--ds-primary-soft)',
                                borderRadius: '0 0 var(--ds-radius-md) var(--ds-radius-md)',
                                padding: '20px 24px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 20,
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 20
                                }}>
                                    <InlineFieldGroup label="Title">
                                        <Input value={title} onChange={(e) => { setTitle(e.target.value); setPristine(false); }} placeholder="What this opcode does" style={{
                                            fontSize: 'var(--ds-text-md)'
                                        }} />
                                    </InlineFieldGroup>
                                    <InlineFieldGroup label="Category">
                                        <Input value={category} onChange={(e) => { setCategory(e.target.value); setPristine(false); }} placeholder="e.g. customer-support" />
                                    </InlineFieldGroup>
                                </div>
                                <InlineFieldGroup label="Description" hint={FIELD_HELP.description}>
                                    <LongTextField
                                        label="Description"
                                        value={description}
                                        onChange={(v) => { setDescription(v); setPristine(false); }}
                                        placeholder="What this procedure does"
                                        monospace={false}
                                    />
                                </InlineFieldGroup>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 20,
                                    borderTop: '1px solid var(--ds-divider)',
                                    paddingTop: 18
                                }}>
                                    <InlineFieldGroup label="Version">
                                        <Input value={version} onChange={(e) => { setVersion(e.target.value); setPristine(false); }} placeholder="0.1.0" />
                                    </InlineFieldGroup>
                                    <InlineFieldGroup label="Default Step Timeout (ms)">
                                        <Input
                                            type="number"
                                            value={defaultStepTimeoutMs ?? ''}
                                            onChange={(e) => { setDefaultStepTimeoutMs(e.target.value === '' ? undefined : Number(e.target.value)); setPristine(false); }}
                                            placeholder="e.g. 30000"
                                        />
                                    </InlineFieldGroup>
                                </div>
                            </div>
                        </section>
                    </section>{/* end ds-opcode-identity */}

                    {/* Step / other-tab detail — order-3 on mobile, col-2 row-2 on desktop */}
                    <div className="ds-opcode-detail space-y-4 p-4 md:p-6">
                        {renderStepDetail()}
                    </div>
                </div>
                )}

                {/* Step kind picker dialog */}
                <OpcodeStepKindPicker
                    open={showKindPicker}
                    onOpenChange={(open) => {
                        setShowKindPicker(open);
                        if (!open) setKindPickerTarget(null);
                    }}
                    onSelect={handleKindSelect}
                />

                {/* JSON preview / editor */}
                <EntityJsonView
                    open={showJson}
                    onOpenChange={setShowJson}
                    title="Opcode JSON"
                    entity={{
                        title,
                        description,
                        version,
                        category,
                        tags,
                        default_step_timeout_ms: defaultStepTimeoutMs,
                        inputs,
                        outputs,
                        steps,
                        definitions,
                        flows,
                        hooks,
                        risk_level: riskLevel,
                        requires_approval: requiresApproval,
                        pii_handling: piiHandling,
                        compliance_tags: complianceTags
                    }}
                    sanitize={false}
                    onApply={(d) => {
                        if (!d || typeof d !== 'object') return;
                        setTitle(d.title || d.name || '');
                        setDescription(d.description || '');
                        setVersion(d.version || '0.1.0');
                        setCategory(d.category || '');
                        setTags(Array.isArray(d.tags) ? d.tags : []);
                        setDefaultStepTimeoutMs(typeof d.default_step_timeout_ms === 'number' ? d.default_step_timeout_ms : undefined);
                        setInputs(Array.isArray(d.inputs) ? d.inputs : []);
                        setOutputs(Array.isArray(d.outputs) ? d.outputs : []);
                        setSteps(Array.isArray(d.steps) ? d.steps : []);
                        setDefinitions(Array.isArray(d.definitions) ? d.definitions : []);
                        setFlows(Array.isArray(d.flows) ? d.flows : []);
                        setHooks(d.hooks && typeof d.hooks === 'object' ? d.hooks : undefined);
                        setRiskLevel(d.risk_level || 'moderate');
                        setRequiresApproval(!!d.requires_approval);
                        setPiiHandling(Array.isArray(d.pii_handling) ? d.pii_handling : []);
                        setComplianceTags(Array.isArray(d.compliance_tags) ? d.compliance_tags : []);
                        setPristine(false);
                    }}
                />
            </div>

            {/* Fixed unsaved-changes bar. This editor is a card embedded in the
                scrolling workspace page (not a full-height shell), and the card
                is overflow-hidden — so `sticky` has nothing to stick to. A fixed
                viewport-bottom bar is the reliable way to keep the Save action
                in view while the user edits a step far down the page. */}
            {!pristine && (
                <div
                    className="flex flex-wrap items-center gap-3 border-t border-amber-300/70 bg-amber-50 px-6 py-2.5 text-[12.5px] text-amber-900 shadow-[0_-2px_12px_rgba(0,0,0,0.1)]"
                    style={{
                        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50
                    }}
                >
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium">
                        “{title || 'This opcode'}” has unsaved changes — save to apply them.
                    </span>
                    <div className="ml-auto">
                        <Button onClick={handleSave} disabled={saving || pristine}>
                            {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                            Save opcode
                        </Button>
                    </div>
                </div>
            )}
        </V2OpCodeBuilderProvider>
    );
});

export default forwardRef(function InlineOpcodeEditor(props, ref) {
    return (
        <OpcodeEditorModeProvider>
            <InlineOpcodeEditorInner ref={ref} {...props} />
        </OpcodeEditorModeProvider>
    );
});

/* ---------------- Subcomponents ---------------- */

function StepPane({
    idx, step, onPatchStep, onChangeKind, definitions, flows, oelContext, paneLabel = 'Step'
}) {
    const meta = KIND_INDEX[step.kind] || KIND_INDEX.llm;
    const Icon = meta.icon;
    const listLabel = formatStepListLabel(step);
    const kindHelp = getStepKindHelp(step.kind);
    const useZoneLayout = ZONE_LAYOUT_STEP_KINDS.has(step.kind);
    const titlePlaceholder = STEP_TITLE_PLACEHOLDERS[step.kind] || 'e.g. My step';
    return (
        <section className={cn(
            'rounded-lg shadow-sm',
            useZoneLayout
                ? 'border-0 bg-[#FFFCF8]'
                : 'border bg-card'
        )}>
            <div className="flex items-center gap-3 border-b bg-muted/30 px-4 py-3">
                <span className={cn('inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted', meta.color)}>
                    <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {paneLabel} {idx + 1} · <span className="font-mono normal-case text-muted-foreground">{step.id}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="text-base font-semibold">{listLabel}</div>
                        {kindHelp && (
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                                            aria-label={`About ${stepKindLabel(step.kind)} steps`}
                                        >
                                            <HelpCircle className="h-3.5 w-3.5" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent
                                        side="bottom"
                                        align="start"
                                        className="max-w-xs border bg-popover px-3 py-2 text-[11px] leading-snug text-popover-foreground shadow-md"
                                    >
                                        {kindHelp}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Kind</Label>
                <Select value={step.kind} onValueChange={onChangeKind}>
                    <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {STEP_KINDS.map((k) => (
                            <SelectItem key={k.kind} value={k.kind}>{formatStepKindOptionLabel(k.kind)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className={cn(
                'p-4',
                useZoneLayout ? 'space-y-3' : 'space-y-4'
            )}>
                {useZoneLayout ? (
                    <LlmStepZone
                        variant="identity"
                        title="Identity"
                        description="Give your step a name so you can reference it later in the procedure and in expressions."
                    >
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,220px)]">
                            <div>
                                <Label className="text-[11px] font-semibold capitalize text-foreground/70">
                                    Step title
                                </Label>
                                <Input
                                    value={step.description || ''}
                                    onChange={(e) => onPatchStep({
                                        ...step,
                                        description: e.target.value
                                    })}
                                    placeholder={titlePlaceholder}
                                    className={cn('mt-1 text-sm', llmFocusInputClass)}
                                />
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold capitalize text-foreground/70">
                                    Step ID
                                </Label>
                                <Input
                                    value={step.id || ''}
                                    onChange={(e) => onPatchStep({
                                        ...step,
                                        id: e.target.value
                                    })}
                                    className={cn('mt-1 font-mono text-sm', llmFocusInputClass)}
                                />
                            </div>
                        </div>
                        <p className="text-[11px] leading-relaxed text-muted-foreground">
                            Shows as{' '}
                            <span className="font-medium text-foreground">{listLabel}</span>
                            {' · '}use{' '}
                            <span className="font-mono font-medium text-primary/90">{step.id || 'step_id'}</span>
                            {' '}in @-expressions
                        </p>
                    </LlmStepZone>
                ) : (
                    <>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Step title</Label>
                                <Input
                                    value={step.description || ''}
                                    onChange={(e) => onPatchStep({
                                        ...step,
                                        description: e.target.value
                                    })}
                                    placeholder="e.g. Ask AI"
                                    className="mt-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Step ID</Label>
                                <Input
                                    value={step.id || ''}
                                    onChange={(e) => onPatchStep({
                                        ...step,
                                        id: e.target.value
                                    })}
                                    className="mt-1.5 font-mono text-sm"
                                />
                            </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            Shown in the procedure list as <span className="font-medium">{listLabel}</span>
                            {' · '}reference in expressions as <span className="font-mono font-medium">{step.id || 'step_id'}</span>
                        </p>
                    </>
                )}
                {dispatchInlineStep({
                    step,
                    onPatchStep,
                    definitions,
                    flows,
                    oelContext
                })}
            </div>
        </section>
    );
}

function OtherBlock({
    title, badge, children
}) {
    return (
        <section className="rounded-lg border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
                <span className="flex-1 text-sm font-semibold">{title}</span>
                {badge && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {badge}
                    </span>
                )}
            </div>
            <div className="p-5">{children}</div>
        </section>
    );
}

/* ---------------- Helpers ---------------- */

function safeJson(s) {
    try { return JSON.parse(s); } catch { return null; }
}

function InlineFieldGroup({
    label, hint, children
}) {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6
        }}>
            <div style={{
                fontSize: 'var(--ds-text-sm)',
                fontWeight: 600,
                color: 'var(--ds-text)',
                letterSpacing: '0.01em'
            }}>
                {label}
            </div>
            {children}
            {hint && (
                <div style={{
                    fontSize: 'var(--ds-text-xs)',
                    color: 'var(--ds-text-muted)',
                    lineHeight: 1.4
                }}>
                    {hint}
                </div>
            )}
        </div>
    );
}
